import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  listMailTracking,
  mailEventsURL,
  purgeMailTracking,
  type MailTrackingRow,
  type MailCampaign,
  type MailSend,
} from "../api/mailTracking";
import Icon from "../components/icons";
import { ortamCoz } from "../lib/ortam";
import TemizleOnay from "../components/TemizleOnay";

function fmtDate(s?: string | null): string {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return s;
  }
}

function yuzde(pay: number, payda: number): string {
  if (payda <= 0) return "0%";
  return `${Math.round((pay / payda) * 100)}%`;
}

// Sayfa boyu kullanıcı seçimi. Sunucu per_page'i 1–500 arasında kabul ediyor
// (admin_mail.go::List), bu liste onun içinde kalır.
const SAYFA_BOYU_SECENEKLERI = [10, 25, 50, 100];
const SAYFA_BOYU_VARSAYILAN = 25;

// Sıralanabilir sütunlar — anahtarlar sunucudaki beyaz listeyle aynı olmalı.
const SUTUNLAR: { key: string; label: string }[] = [
  { key: "name", label: "Alıcı" },
  { key: "sent", label: "Gönderim" },
  { key: "opened", label: "Açılma" },
  { key: "opens", label: "Tekrar" },
  { key: "", label: "IP" },
  { key: "", label: "Ortam" },
  { key: "clicks", label: "Siteye geliş" },
  { key: "pageviews", label: "Gezilen sayfa" },
];

// Tanıtım e-postası ölçümü listesi.
//
// Açılma ölçümü doğası gereği EKSİKTİR: alıcı görselleri kapalıysa açtığı
// hâlde görünmez. Tıklama ve sayfa gezintisi kesindir.
export default function MailTracking() {
  const navigate = useNavigate();

  const [rows, setRows] = useState<MailTrackingRow[]>([]);
  const [campaigns, setCampaigns] = useState<MailCampaign[]>([]);
  const [sends, setSends] = useState<MailSend[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [canli, setCanli] = useState(false);

  const [campaign, setCampaign] = useState("");
  // Rapor birimi GÖNDERİM: aynı kişi farklı maillerde ayrı satırdır.
  const [sendID, setSendID] = useState(0);
  const [arama, setArama] = useState("");
  const [sort, setSort] = useState("");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [sayfa, setSayfa] = useState(1);
  const [sayfaBoyu, setSayfaBoyu] = useState(SAYFA_BOYU_VARSAYILAN);

  // Ölçüm sıfırlama — geri dönüşsüz, onay diyaloğu arkasında.
  const [temizleAcik, setTemizleAcik] = useState(false);
  const [temizleniyor, setTemizleniyor] = useState(false);
  const [temizleHata, setTemizleHata] = useState<string | null>(null);
  const [temizlendi, setTemizlendi] = useState<string | null>(null);

  // Sorgu parametrelerini ref'te de tutuyoruz: SSE olayı geldiğinde çağrılan
  // tazeleme fonksiyonunun kimliği değişmesin. Aksi hâlde her filtre
  // değişiminde EventSource sökülüp yeniden kurulurdu.
  const sorguRef = useRef({ campaign, sendID, arama, sort, order, sayfa, sayfaBoyu });
  sorguRef.current = { campaign, sendID, arama, sort, order, sayfa, sayfaBoyu };

  const yukle = useCallback(async (sessiz = false) => {
    if (!sessiz) setLoading(true);
    setErr(null);
    const q = sorguRef.current;
    try {
      const d = await listMailTracking({
        campaign: q.campaign,
        sendID: q.sendID,
        q: q.arama,
        sort: q.sort,
        order: q.order,
        page: q.sayfa,
        perPage: q.sayfaBoyu,
      });
      setRows(d.items);
      setTotal(d.total);
      setCampaigns(d.campaigns);
      setSends(d.sends);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Yükleme hatası");
    } finally {
      if (!sessiz) setLoading(false);
    }
  }, []);

  // Arama kutusu her tuşta sunucuya gitmesin: 350 ms sessizlik beklenir.
  useEffect(() => {
    const t = setTimeout(() => void yukle(), 350);
    return () => clearTimeout(t);
  }, [arama, campaign, sendID, sort, order, sayfa, sayfaBoyu, yukle]);

  // Canlı güncelleme: ölçüm ucuna bir açılma/tıklama/sayfa düştüğünde panel
  // kendi geçerli sorgusunu yeniden çalıştırır. Olaylar salkım hâlinde
  // gelebildiği için (tek ziyaret = birkaç sayfa) 800 ms sönümleme var;
  // aksi hâlde her sayfa için ayrı istek giderdi.
  useEffect(() => {
    const es = new EventSource(mailEventsURL());
    let zamanlayici: number | undefined;

    const tazele = () => {
      window.clearTimeout(zamanlayici);
      zamanlayici = window.setTimeout(() => void yukle(true), 800);
    };

    es.onopen = () => setCanli(true);
    es.onerror = () => setCanli(false); // tarayıcı kendiliğinden yeniden bağlanır
    // "purge": başka bir sekmede ölçüm sıfırlandığında bu sekme bayat
    // listeye bakmasın.
    for (const tur of ["open", "click", "pageview", "purge"]) {
      es.addEventListener(tur, tazele);
    }

    return () => {
      window.clearTimeout(zamanlayici);
      es.close();
    };
  }, [yukle]);

  // Ölçümü sıfırla. Sonuç SESSİZ KALMAZ: kaç kayıt gittiği yazılır
  // (ui-admin §5). Filtreler de sıfırlanır — silinmiş bir gönderimi
  // seçili bırakmak "kayıt yok" görüntüsünü hataya benzetirdi.
  async function temizle() {
    setTemizleniyor(true);
    setTemizleHata(null);
    try {
      const d = await purgeMailTracking();
      const s = d.deleted;
      setTemizlendi(
        `Ölçüm sıfırlandı — toplam ${d.total} kayıt silindi: ` +
        `${s.recipients} alıcı, ${s.opens} açılma, ${s.clicks} tıklama, ` +
        `${s.pageviews} sayfa gezintisi, ${s.sends} gönderim. ` +
        `Listeden çıkanlar korundu.`,
      );
      setTemizleAcik(false);
      setCampaign("");
      setSendID(0);
      setArama("");
      setSayfa(1);
      await yukle();
    } catch (e: unknown) {
      setTemizleHata(e instanceof Error ? e.message : "Silinemedi");
    } finally {
      setTemizleniyor(false);
    }
  }

  function sirala(key: string) {
    if (!key) return;
    if (sort === key) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSort(key);
      setOrder("desc");
    }
    setSayfa(1);
  }

  const sonSayfa = Math.max(1, Math.ceil(total / sayfaBoyu));
  const acan = rows.filter((r) => r.opened_at).length;
  const tiklayan = rows.filter((r) => r.click_count > 0).length;
  const gezen = rows.filter((r) => r.pageview_count > 0).length;

  return (
    <div className="page">
      <div className="page-head">
        <div className="page-head-row">
          <h1>Mail Takibi</h1>
          <span className="muted">
            {total} alıcı
            {rows.length > 0 && (
              <>
                {" "}· bu sayfada {acan} açtı ({yuzde(acan, rows.length)}) ·{" "}
                {tiklayan} tıkladı · {gezen} sitede gezindi
              </>
            )}{" "}
            <span
              className={"badge " + (canli ? "ok" : "off")}
              title={canli ? "Yeni açılma ve ziyaretler anında düşer" : "Canlı bağlantı yok — Yenile'yi kullanın"}
            >
              {canli ? "canlı" : "bağlantı yok"}
            </span>
          </span>
        </div>
        <p className="page-sub">
          Tanıtım e-postalarının açılma ve siteye geliş ölçümü. Açılma, mesaja
          gömülü görselin yüklenmesiyle anlaşılır; alıcı <strong>görselleri
          kapalıysa açtığı hâlde görünmez</strong> — “açılmadı” kesin bilgi
          değildir. Tıklama ve gezilen sayfalar ise kesindir. Bir satıra
          tıklayarak ziyaretin tamamını sırayla görebilirsiniz.
        </p>
      </div>

      <div className="toolbar">
        {/* Yıkıcı eylem SOLDA, birincil eylemin (Yenile) yanında değil —
            ui-admin §5: yanlış düğmeye kayma riski.
            total=0'da KAPATILMAZ: alıcısı olmayan anonim ziyaretçi kayıtları
            aynı tablodadır ve bu liste onları saymaz. Kapatılsaydı "Sitede
            Gezinenler" dolu iken temizleme düğmesi erişilemez olurdu. */}
        <button
          type="button"
          className="btn-danger"
          onClick={() => { setTemizleHata(null); setTemizleAcik(true); }}
          disabled={loading || temizleniyor}
          title="Tüm ölçüm kayıtlarını sil (geri alınamaz)"
        >
          <Icon name="trash" size={15} />
          Kayıtları temizle
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginLeft: "auto" }}>
          {/* Gönderim seçici: her mail ayrı rapordur (kampanya + konu + gün). */}
          {sends.length > 0 && (
            <select
              value={sendID}
              onChange={(e) => { setSendID(Number(e.target.value)); setSayfa(1); }}
              title="Her gönderim ayrı rapordur"
              style={{ maxWidth: 380 }}
            >
              <option value={0}>Tüm gönderimler ({total})</option>
              {sends.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.send_day} · {g.subject || g.campaign} — {g.recipients} alıcı,{" "}
                  {g.opened} açtı, {g.clicked} tıkladı
                </option>
              ))}
            </select>
          )}
          {campaigns.length > 1 && (
            <select
              value={campaign}
              onChange={(e) => { setCampaign(e.target.value); setSayfa(1); }}
            >
              <option value="">Tüm kampanyalar</option>
              {campaigns.map((c) => (
                <option key={c.campaign} value={c.campaign}>
                  {c.campaign} ({c.recipients})
                </option>
              ))}
            </select>
          )}
          <button className="btn-primary" onClick={() => void yukle()} disabled={loading}>
            Yenile
          </button>
        </div>
      </div>

      {err && <div className="error">{err}</div>}
      {temizlendi && (
        <div className="notice" role="status">
          {temizlendi}{" "}
          <button type="button" className="link" onClick={() => setTemizlendi(null)}>Kapat</button>
        </div>
      )}

      <div className="data-table-card">
        {/* Arama kartın İÇİNDE — filtre, filtrelediği listeyle aynı yüzeyde. */}
        <div className="data-table-toolbar">
          <label className="data-search">
            <Icon name="search" size={15} />
            <input
              type="search"
              placeholder="E-posta veya ad ara…"
              value={arama}
              onChange={(e) => { setArama(e.target.value); setSayfa(1); }}
              aria-label="Mail takibinde ara"
            />
          </label>
          <span className="data-count">{total} kayıt</span>
        </div>
        {loading ? (
          <div className="empty-state"><div className="spinner-sm" /><p>Yükleniyor…</p></div>
        ) : rows.length === 0 ? (
          <div className="empty-state">
            <Icon name="mail" size={34} />
            <p>{arama ? "Aramaya uyan kayıt yok." : "Henüz takip kaydı yok."}</p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                {SUTUNLAR.map((s, i) => (
                  <th
                    key={i}
                    onClick={() => sirala(s.key)}
                    style={s.key ? { cursor: "pointer", userSelect: "none" } : undefined}
                    title={s.key ? "Sıralamak için tıklayın" : undefined}
                  >
                    {s.label}
                    {s.key && sort === s.key && (order === "asc" ? " ▲" : " ▼")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => navigate(`/mail-takip/${r.id}`)}
                  style={{ cursor: "pointer" }}
                  title="Ziyaretin tamamını gör"
                >
                  <td>
                    <div>{r.name || "—"}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{r.email}</div>
                  </td>
                  <td>{fmtDate(r.sent_at)}</td>
                  <td>
                    {r.opened_at
                      ? <span className="badge ok">{fmtDate(r.opened_at)}</span>
                      : <span className="badge off">açılmadı</span>}
                  </td>
                  <td>
                    {r.open_count > 1
                      ? <span className="badge warn">{r.open_count}×</span>
                      : <span className="muted">—</span>}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>{r.open_ip || "—"}</td>
                  <td className="muted" style={{ fontSize: 12 }} title={ortamCoz(r.open_agent).detay}>{ortamCoz(r.open_agent).ozet}</td>
                  <td>
                    {r.click_count > 0
                      ? <span className="badge ok">{r.click_count}× · {fmtDate(r.last_click_at)}</span>
                      : <span className="muted">—</span>}
                  </td>
                  <td>
                    {r.pageview_count > 0
                      ? <span className="badge ok">{r.pageview_count} sayfa</span>
                      : <span className="muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Sayfa boyu seçici, SAYFA SAYISINDAN BAĞIMSIZ çizilir: gezinme
          düğmeleriyle birlikte gizlenseydi 100'ü seçip tek sayfaya düşen
          kullanıcı 10'a geri dönemezdi — kontrolü kendi etkisi yok ederdi.
          Yalnız hiç kayıt yokken tümü gizlenir; orada seçecek bir şey yok. */}
      {total > 0 && (
      <div className="toolbar pager">
        <label className="pager-boy">
          Sayfada
          {/* aria-label YOK: erişilebilir ad sarmalayan <label>'ın görünen
              metninden gelsin (WCAG 2.5.3 — ad, görünen etiketi içermeli). */}
          <select
            value={sayfaBoyu}
            onChange={(e) => { setSayfaBoyu(Number(e.target.value)); setSayfa(1); }}
            title="Sayfa başına kaç kayıt gösterilsin"
          >
            {SAYFA_BOYU_SECENEKLERI.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          kayıt
        </label>

        {sonSayfa > 1 ? (
          <div className="pager-nav">
            <button className="btn-primary" disabled={sayfa <= 1} onClick={() => setSayfa(1)}>« İlk</button>
            <button className="btn-primary" disabled={sayfa <= 1} onClick={() => setSayfa((s) => s - 1)}>‹ Önceki</button>
            <span className="muted">Sayfa {sayfa} / {sonSayfa} ({total} kayıt)</span>
            <button className="btn-primary" disabled={sayfa >= sonSayfa} onClick={() => setSayfa((s) => s + 1)}>Sonraki ›</button>
            <button className="btn-primary" disabled={sayfa >= sonSayfa} onClick={() => setSayfa(sonSayfa)}>Son »</button>
          </div>
        ) : (
          <span className="muted">Tek sayfa · {total} kayıt</span>
        )}
      </div>
      )}

      <TemizleOnay
        acik={temizleAcik}
        baslik="Tüm ölçüm kayıtları silinsin mi?"
        eylemEtiketi="Evet, ölçümü sıfırla"
        calisiyor={temizleniyor}
        hata={temizleHata}
        onIptal={() => setTemizleAcik(false)}
        onOnay={() => void temizle()}
      >
        <p>
          Bu işlem <strong>geri alınamaz.</strong> Yedek alınmaz; silinen ölçüm
          geri getirilemez.
        </p>
        <div className="danger-scope">
          <strong>Silinecekler:</strong>
          <ul>
            <li><strong>{total}</strong> alıcı kaydı (token'lar dâhil) ve açılma ölçümü</li>
            <li>Tüm tıklamalar ve gezilen sayfalar</li>
            <li><strong>{sends.length}</strong> gönderim raporu</li>
            <li>
              <strong>“Sitede Gezinenler”</strong> sayfası da boşalır — anonim
              ziyaretçi kayıtları aynı tablodadır
            </li>
          </ul>
        </div>
        <p>
          <strong>Korunanlar:</strong> listeden çıkanlar (unsubscribe). Ölçüm
          sıfırlanır, <em>rıza sıfırlanmaz</em> — aksi hâlde gönderim betiği o
          adresleri ayıklayamaz ve o kişilere tekrar mail giderdi.
        </p>
        <p>
          Daha önce gönderilmiş maillerdeki bağlantılar <strong>ziyaretçiyi
          kırmaz</strong>: piksel yine görüntü döner, yönlendirme yine çalışır —
          yalnız yeni ölçüm üretmezler.
        </p>
      </TemizleOnay>
    </div>
  );
}
